<template>
  <v-dialog
    v-model="showAddDialog"
    max-width="500px"
    class="lightning-model-page__add-new-model-dialog"
  >
    <v-card>
      <v-card-title>Add New Page</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="Year"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedMake"
              :items="makes"
              label="Make"
              outlined
              dense
              :disabled="selectedYear === null"
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedModel"
              :items="models"
              label="Model"
              outlined
              dense
              :disabled="selectedYear === null || selectedMake === null"
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" class="mt-0 py-0 text-error" v-if="pageError">*{{ pageError }}</v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="error" variant="text" @click="cancelNewPageAddition"> Cancel </v-btn>
        <v-btn
          color="primary"
          @click="addNewPage"
          :disabled="selectedModel === null || !!pageError"
        >
          Add Page
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import {
  fetchYearsFromChromeData,
  fetchMakesByYearFromChromeData,
  fetchModelsByYearAndMakeFromChromeData,
} from '@/apis'
import type { IPage } from '@/types'
import { useProductSettingsStore } from '@/stores/productSettings'
import { getDefaultCTASettings } from '@/stores/utils'
import { useAccountSettingsStore } from '@/stores/accountSettings'

const showAddDialog = defineModel<boolean>({ default: false })
const accountSettings = useAccountSettingsStore()

const emit = defineEmits<{
  (event: 'add-new-page', newPage: IPage): void
}>()

const store = useProductSettingsStore()
const pageError = ref<string | null>(null)

const selectedYear = ref<number | null>(null)
const selectedMake = ref<string | null>(null)
const selectedModel = ref<string | null>(null)

const years = ref<number[]>([])
const makes = ref<string[]>([])
const models = ref<string[]>([])

const addNewPage = () => {
  if (!selectedYear.value || !selectedMake.value || !selectedModel.value) return

  pageError.value = null

  const newPage: IPage = {
    year: selectedYear.value,
    make: selectedMake.value,
    model: selectedModel.value,
    path: `/${selectedYear.value}/${selectedMake.value}/${selectedModel.value}`,
    status: true,
    highlightedFeatures: [],
    primaryCTA: getDefaultCTASettings('View Inventory', accountSettings.websiteUrl),
    secondaryCTA: getDefaultCTASettings('Order Now', accountSettings.websiteUrl),
    overrideGlobalPrimaryCTA: false,
    overrideGlobalSecondaryCTA: false,
  }

  emit('add-new-page', newPage)
}

const cancelNewPageAddition = () => {
  pageError.value = null
  console.log('Cancelling new page addition')
  selectedYear.value = years.value[0]
  selectedMake.value = null
  selectedModel.value = null
  showAddDialog.value = false
}

watch(selectedYear, async (year) => {
  if (year) {
    makes.value = (await fetchMakesByYearFromChromeData(year)) as string[]
    makes.value.sort()
    selectedMake.value = null
    selectedModel.value = null
  }
})

watch(selectedMake, async (make) => {
  if (make && selectedYear.value) {
    models.value = (await fetchModelsByYearAndMakeFromChromeData(
      selectedYear.value,
      make,
    )) as string[]
    models.value.sort()
    selectedModel.value = null
  }
})

watch(selectedModel, () => {
  if (selectedModel.value) {
    if (
      store.settings?.pages.some(
        (page) =>
          page.path === `/${selectedYear.value}/${selectedMake.value}/${selectedModel.value}`,
      )
    ) {
      pageError.value = 'Page already exists'
    } else {
      pageError.value = null
    }
  }
})

// Fetch Initial Data
onMounted(async () => {
  years.value = (await fetchYearsFromChromeData()) as number[]
  years.value.sort().reverse()
  selectedYear.value = years.value[0]
})
</script>

<style scoped>
.lightning-model-page__add-new-model-dialog :deep(input[type='text']) {
  border: none;
  background-color: transparent;
  box-shadow: none;
}

.lightning-model-page__add-new-model-dialog :deep(input[type='text']:focus) {
  border: none;
  background-color: transparent;
  box-shadow: none;
}
</style>
